import React, { useState, useEffect, useRef } from 'react';
import CalendarGrid from '../CalendarGrid';
import {
  addMonths,
  subMonths,
  format,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { capitalize } from '../../../utilities/functions';
import { FilterChipOutlined } from '../../common/ActionChip/FilterChip';
import { DateRange } from '../Utils';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import './index.css'
import '../calendar-modal.css'

interface SimpleCalendarRangeProps {
  active?: boolean
  textControlButton?: string
  onConfirm: (range: DateRange) => void;
  initialFrom?: Date;
  initialTo?: Date;
}

const BasicCalendarRange: React.FC<SimpleCalendarRangeProps> = ({
  active = false,
  textControlButton = 'Calendario',
  onConfirm,
  initialFrom,
  initialTo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const today = new Date();
  const [from, setFrom] = useState(initialFrom ?? today);
  const [to, setTo] = useState(initialTo ?? today);
  const [activeTab, setActiveTab] = useState<'from' | 'to'>('from');
  const [visibleMonth, setVisibleMonth] = useState(from);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(calendarRef, () => setIsOpen(false), buttonRef);

  useEffect(() => {
    if (activeTab === 'from') {
      setVisibleMonth(from);
    } else {
      setVisibleMonth(to >= from ? to : from);
    }
  }, [activeTab, from, to]);

  const handleSelect = (date: Date) => {
    if (activeTab === 'from') {
      setFrom(date);
      setTo(date);
      setActiveTab('to');
    } else {
      setTo(date);
    }
  };

  return (
    <div className='calendar-wrapper'>

      <span ref={buttonRef} className='calendar-wrapper-btn-control' onClick={() => setIsOpen(!isOpen)}>
        <FilterChipOutlined
          active={active}
          label={textControlButton}
          onClick={() => setIsOpen(!isOpen)}
        />
      </span>

      <div ref={calendarRef} className={`calendar-modal ${isOpen ? 'visible' : 'hidden'}`}>

        <div className="basic-calendar-range">
          <div className="tabs-container">
            <div className="tabs-wrapper">
              <div className={`tabs-indicator ${activeTab === 'from' ? 'left' : 'right'}`} />
              <button
                className={`tab-button ${activeTab === 'from' ? 'active' : ''}`}
                onClick={() => setActiveTab('from')}
              >
                Desde
              </button>
              <button
                className={`tab-button ${activeTab === 'to' ? 'active' : ''}`}
                onClick={() => setActiveTab('to')}
              >
                Hasta
              </button>
            </div>
          </div>

          <div className="calendar-summary">
            {format(from, 'd MMM yyyy', { locale: es })} – {format(to, 'd MMM yyyy', { locale: es })}
          </div>


          <div className="month-header">
            <button onClick={() => setVisibleMonth(subMonths(visibleMonth, 1))}>{'<'}</button>
            <span>{capitalize(format(visibleMonth, 'MMMM yyyy', { locale: es }))}</span>
            <button onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}>{'>'}</button>
          </div>

          <div className="calendar-range-container">
            <div className={`calendar-range-slider ${activeTab === 'from' ? 'show-from' : 'show-to'}`}>
              <div className='calendar-range-slide-box'>
                <div className="calendar-range-slide from">
                  <CalendarGrid
                    visibleMonth={visibleMonth}
                    from={from}
                    to={to}
                    onSelect={handleSelect}
                  />
                </div>
              </div>

              <div className="calendar-range-slide-box">
                <div className="calendar-range-slide to">
                  <CalendarGrid
                    visibleMonth={visibleMonth}
                    from={from}
                    to={to}
                    onSelect={handleSelect}
                    disablePastFrom={true}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="calendar-footer">
            <button className='calendar-footer-btn-cancel'
              onClick={() => { setIsOpen(false); setActiveTab('from') }}>Cancelar
            </button>
            <button
              className='calendar-footer-btn-confirm'
              onClick={() => {
                onConfirm({ from: format(from, 'yyyy-MM-dd'), to: format(to, 'yyyy-MM-dd') });
                setActiveTab('from');
                setIsOpen(false);
              }}
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>

  );
};

export default BasicCalendarRange;
